import { useState, useEffect } from "react";
import api from "../services/api";
import { ENDPOINTS } from "../utils/endpoints";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

export const useSections = () => {
  const [sections, setSections] = useState([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [newSection, setNewSection] = useState("");
  const [isCreatingSection, setIsCreatingSection] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState("");
  const [isRenamingSection, setIsRenamingSection] = useState(false);
  const [deletingSectionId, setDeletingSectionId] = useState(null);

  const fetchSections = async () => {
    try {
      const { data } = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
      if (data.success) {
        setSections(data.sections);
      }
    } catch (err) {
      console.error("Failed to fetch sections", err);
    }
  };

  const handleCheckboxChange = (section) => {
    setSelectedSectionIds((prev) =>
      prev.includes(section.id)
        ? prev.filter((s) => s !== section.id)
        : [...prev, section.id],
    );
  };

  const handleCreateSection = async () => {
    if (isCreatingSection || !newSection.trim()) return;

    setIsCreatingSection(true);

    try {
      const payload = { title: newSection };
      const { data } = await api.post(ENDPOINTS.CREATE_SECTION, payload);

      if (data.success) {
        await fetchSections();
        const newSectionCreated = sections.find((s) => s.title === newSection);
        if (newSectionCreated) {
          handleCheckboxChange(newSectionCreated);
        }
        setNewSection("");
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating section");
    } finally {
      setIsCreatingSection(false);
    }
  };

  const handleStartEdit = (section) => {
    setEditingSectionId(section.id);
    setEditingSectionTitle(section.title);
  };

  const handleCancelEdit = () => {
    setEditingSectionId(null);
    setEditingSectionTitle("");
  };

  const handleSaveRename = async (sectionId) => {
    if (!editingSectionTitle.trim()) {
      toast.error("Section name cannot be empty");
      return;
    }

    if (isRenamingSection) return;

    setIsRenamingSection(true);

    try {
      const payload = { title: editingSectionTitle.trim() };
      const { data } = await api.put(
        ENDPOINTS.UPDATE_SECTION_BY_ID(sectionId),
        payload,
      );

      if (data.success) {
        toast.success("Section renamed successfully");
        await fetchSections();
        setEditingSectionId(null);
        setEditingSectionTitle("");
      } else {
        toast.error(data.message || "Failed to rename section");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error renaming section");
    } finally {
      setIsRenamingSection(false);
    }
  };

  const handleDeleteSection = (sectionId) => {
    if (deletingSectionId) return;

    confirmAlert({
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this section?\n\nThis action cannot be undone.",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            setDeletingSectionId(sectionId);

            try {
              const { data } = await api.delete(
                ENDPOINTS.DELETE_SECTION_BY_ID(sectionId),
              );

              if (data.success) {
                toast.success("Section deleted successfully");
                setSelectedSectionIds((prev) =>
                  prev.filter((id) => id !== sectionId),
                );
                await fetchSections();
              } else {
                toast.error(data.message || "Failed to delete section");
              }
            } catch (err) {
              console.error(err);
              const errorMessage =
                err.response?.data?.message ||
                "Error deleting section. This section may be in use by channels or communities.";
              toast.error(errorMessage);
            } finally {
              setDeletingSectionId(null);
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.info("Section deletion cancelled"),
        },
      ],
    });
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const filteredSections = sections.filter((section) =>
    section.title.toLowerCase().includes(searchText.toLowerCase()),
  );

  return {
    sections,
    selectedSectionIds,
    setSelectedSectionIds,
    searchText,
    setSearchText,
    newSection,
    setNewSection,
    isCreatingSection,
    editingSectionId,
    editingSectionTitle,
    setEditingSectionTitle,
    isRenamingSection,
    deletingSectionId,
    filteredSections,
    handleCheckboxChange,
    handleCreateSection,
    handleStartEdit,
    handleCancelEdit,
    handleSaveRename,
    handleDeleteSection,
  };
};
